import type { CustomerReferenceTag, OrderCondition, ProductClinicChain, ScalePrice } from '~/types'

export interface ShoppingListRes {
  groups: string[]
  items: Groups[]
  suppliers: string[]
}

export interface Groups {
  groupName: string
  suppliers: ShoppingListSupplier[]
}

export interface ShoppingListSupplier {
  handle: string
  possibleReferences: CustomerReferenceTag[]
  products: ShoppingListProduct[]
}

export type ShoppingListScalePrices = ScalePrice & {
  reference: CustomerReferenceTag | null
}

export interface Campaign {
  name: string
  status: string
  endDate: string
  discountPercent: number
  discountType: string
  beforePrice: number
  afterPrice: number
}

export interface ShoppingListProduct {
  supplierHandle: string
  groupName?: string | null
  name: string
  handle: string
  idExternal: string
  imageUrl: string
  availability: string
  bestBeforeDate?: string
  listPrice: number
  scalePrices: ShoppingListScalePrices[]
  clinicChain: ProductClinicChain | null
  orderCondition: OrderCondition | null
  preselectedReference: CustomerReferenceTag | null
  isVisible: boolean
  quantity: number
  showOrderOptimizationChart: boolean
  lastOrderedQuantity: number
  lastOrderedDate?: string
  campaign?: Campaign
}

function mergeShoppingListsWithResponse(shoppingLists: Ref<Array<Groups>>, respondedGroups: Array<Groups>, removableProducts: Array<string> = []) {
  const groups = new Map(unref(shoppingLists).map(g => [g.groupName, g]))
  for (const newGroup of respondedGroups) {
    const g: Groups | undefined = groups.get(newGroup.groupName)
    if (!g) {
      unref(shoppingLists).push(newGroup)
      groups.set(newGroup.groupName, newGroup)
      continue
    }
    const suppliers = new Map(g.suppliers.map(s => [s.handle, s]))
    for (const newSupplier of newGroup.suppliers) {
      const target = suppliers.get(newSupplier.handle)
      if (target) {
        const products = new Set(target.products.map(p => p.handle))
        for (const newProduct of newSupplier.products) {
          const exists = products.has(newProduct.handle)
          if (exists) continue
          else target.products.push(newProduct)
        }

        // Remove products that are no longer available
        target.products = target.products.filter(p => !removableProducts.includes(p.handle))
      }
      else {
        g.suppliers.push(newSupplier)
        suppliers.set(newSupplier.handle, newSupplier)
      }
    }
  }
}

function excludeInvisibleSupplierHandles(data: { suppliers: Array<string> }, excluded: Set<string>) {
  return (data.suppliers ?? []).filter(
    (handle: string) => !excluded.has(handle),
  )
}

function includeAllowedSuppliers(data: { items: Array<Groups> }, allowed: Set<string>): Groups[] {
  return (data.items ?? []).map((item: Groups) => ({
    ...item,
    suppliers: item.suppliers.filter((s: ShoppingListSupplier) => allowed.has(s.handle)),
  }))
}

export const useShoppingList = defineStore('shopping-list', () => {
  const isLoading = ref(true)
  const log = useLogger('shoppingList')
  const { $snackbar, $i18n } = useNuxtApp()
  const userStore = useUserStore()
  const featureSettingsStore = useFeatureSettingsStore()
  const { baseURL } = useRuntimeConfig().app

  const groupNames = ref<string[]>([])
  const shoppingLists = ref<Array<Groups>>([])
  const availableSuppliers = ref<string[]>([])

  const customerHandle = computed(() => userStore.customerHandle)
  const accessToken = computed(() => userStore.accessToken)
  const gpoHandle = computed(() => userStore.gpoHandle)

  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const query = computed(() => ({ cHandle: unref(customerHandle), groupName: 'default', limit: 12, page: 1 }))
  const abortIfNotAuthenticated = useAbortIfNotAuthenticated()
  const { withAuthRetry } = useWithAuthRetry()

  const {
    refresh: refreshInitialFetch,
    error: initialFetchError,
  } = useFetch<ShoppingListRes>(() => '/api/intern/shopping-list', {
    headers: headerFields,
    query,
    baseURL,
    watch: [accessToken],
    ...abortIfNotAuthenticated,
    onResponseError({ response }) {
      log.warn('Failed to fetch shopping lists', { responseStatus: response.status })
      // Fire-and-forget: refreshing the token flips `accessToken`, and the
      // `watch: [accessToken]` above performs the single recovery refetch.
      if (response.status === 401) void userStore.reloadAuth()
      if (response.status === 403) {
        _setDefaultValues()
      }
    },
    onResponse({ response }) {
      log.debug('Initial fetch response received', { response: response?._data })
      const data = response?._data ?? {}
      groupNames.value = data.groups ?? []

      const clinicChainHandle = unref(gpoHandle) ?? 'none'
      const excluded = new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
      availableSuppliers.value = excludeInvisibleSupplierHandles(data, excluded)

      const allowed = new Set(availableSuppliers.value)
      shoppingLists.value = includeAllowedSuppliers(data, allowed)

      isLoading.value = false
    },
  })

  async function load(group: string, page: number, limit: number = 12, supplierHandle?: string, showSkeleton: boolean = true) {
    if (!unref(accessToken)) return
    if (showSkeleton) {
      isLoading.value = true
    }

    try {
      const response = await withAuthRetry(() => $fetch<ShoppingListRes>('/api/intern/shopping-list', {
        headers: { Authorization: `Bearer ${unref(accessToken)}` },
        query: { cHandle: unref(customerHandle), groupName: group, limit, page, sHandle: supplierHandle },
        baseURL,
        onResponseError({ response }) {
          if (response.status !== 401) log.warn('Failed to load shopping lists', { responseStatus: response.status })
          if (response.status === 403) {
            _setDefaultValues()
          }
        },
      }))

      if (response) {
        groupNames.value = response.groups ?? []

        const clinicChainHandle = unref(gpoHandle) ?? 'none'
        const excluded = new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
        availableSuppliers.value = excludeInvisibleSupplierHandles(response, excluded)

        mergeShoppingListsWithResponse(shoppingLists, includeAllowedSuppliers(response, new Set(availableSuppliers.value)))
      }
    }
    finally {
      isLoading.value = false
    }
  }

  async function addToShoppingList(group: string, productHandles: string[], quantity?: number) {
    if (!unref(accessToken)) return
    try {
      isLoading.value = true
      const response = await withAuthRetry(() => $fetch<ShoppingListRes>('/api/intern/shopping-list', {
        method: 'POST',
        headers: { Authorization: `Bearer ${unref(accessToken)}` },
        query: { cHandle: unref(customerHandle) },
        baseURL,
        body: {
          group,
          products: productHandles.map(handle => ({
            handle,
            quantity: quantity ?? 1,
          })),
        },
        onResponseError({ response }) {
          if (response.status !== 401) log.warn('Failed to add a product to shopping lists', { responseStatus: response.status })
          if (response.status === 403) {
            _setDefaultValues()
          }
        },
      }))
      if (response) {
        groupNames.value = response.groups ?? []

        const clinicChainHandle = unref(gpoHandle) ?? 'none'
        const excluded = new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
        availableSuppliers.value = excludeInvisibleSupplierHandles(response, excluded)

        mergeShoppingListsWithResponse(shoppingLists, includeAllowedSuppliers(response, new Set(availableSuppliers.value)))
      }
    }
    finally {
      isLoading.value = false
    }
  }

  async function removeFromShoppingList(productHandles: Array<string>, showSnackBar: boolean = false, group?: string) {
    if (!unref(accessToken)) return
    try {
      const response = await withAuthRetry(() => $fetch<ShoppingListRes>('/api/intern/shopping-list', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${unref(accessToken)}` },
        query: { cHandle: unref(customerHandle) },
        baseURL,
        body: {
          group,
          products: productHandles,
        },
        onResponseError({ response }) {
          if (response.status !== 401) log.warn('Failed to remove a product from shopping lists', { responseStatus: response.status })
          if (response.status === 403) {
            _setDefaultValues()
          }
        },
      }))
      if (response) {
        const data = response ?? {}
        groupNames.value = data.groups ?? []

        const clinicChainHandle = unref(gpoHandle) ?? 'none'
        const excluded = new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
        availableSuppliers.value = excludeInvisibleSupplierHandles(data, excluded)

        shoppingLists.value = shoppingLists.value.map((g) => {
          const suppliers = g.suppliers.map((s) => {
            const products = s.products.filter(p => !productHandles.includes(p.handle))
            return {
              ...s,
              products,
            }
          }).filter(s => s.products.length > 0)
          return {
            ...g,
            suppliers,
          }
        })
      }
      if (showSnackBar) {
        $snackbar.showMessage({
          text: $i18n.t('shoppingList.removedSnackbar.message'),
          color: 'success',
          closeText: $i18n.t('close'),
        })
      }
    }
    catch (error) {
      log.warn('Failed to remove from shopping list', { error })
    }
  }

  async function deleteShoppingListGroup(groupName: string) {
    if (!unref(accessToken)) return
    try {
      isLoading.value = true
      const response = await withAuthRetry(() => $fetch<ShoppingListRes>('/api/intern/shopping-list', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${unref(accessToken)}` },
        query: { cHandle: unref(customerHandle) },
        baseURL,
        body: {
          group: groupName,
          products: [],
        },
        onResponseError({ response }) {
          if (response.status !== 401) log.warn('Failed to delete a shopping lists', { responseStatus: response.status })
          if (response.status === 403) {
            _setDefaultValues()
          }
        },
      }))

      // Update local state
      if (response) {
        groupNames.value = response.groups ?? []

        const clinicChainHandle = unref(gpoHandle) ?? 'none'
        const excluded = new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
        availableSuppliers.value = excludeInvisibleSupplierHandles(response, excluded)

        shoppingLists.value = shoppingLists.value.filter(g => g.groupName !== groupName)
      }

      $snackbar.showMessage({
        text: $i18n.t('shoppingList.deletedProduct.message'),
        color: 'success',
        closeText: $i18n.t('close'),
      })
    }
    catch (error) {
      log.warn('Failed to delete shopping list group', { error })
    }
    finally {
      isLoading.value = false
    }
  }

  async function renameShoppingListGroup(oldName: string, newName: string) {
    if (!unref(accessToken)) return
    if (!oldName || !newName || oldName === newName) {
      return
    }

    try {
      isLoading.value = true
      const response = await withAuthRetry(() => $fetch<ShoppingListRes>('/api/intern/shopping-list', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${unref(accessToken)}` },
        query: { cHandle: unref(customerHandle) },
        baseURL,
        body: {
          group: newName,
          oldGroup: oldName,
        },
        onResponseError({ response }) {
          if (response.status !== 401) log.warn('Failed to rename a shopping lists', { responseStatus: response.status })
          if (response.status === 403) {
            _setDefaultValues()
          }
        },
      }))

      if (response) {
        groupNames.value = response.groups ?? []

        const clinicChainHandle = unref(gpoHandle) ?? 'none'
        const excluded = new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
        availableSuppliers.value = excludeInvisibleSupplierHandles(response, excluded)

        shoppingLists.value = shoppingLists.value.filter(g => g.groupName !== oldName)
        mergeShoppingListsWithResponse(shoppingLists, includeAllowedSuppliers(response, new Set(availableSuppliers.value)))
      }

      $snackbar.showMessage({
        text: $i18n.t('shoppingList.groupRenamedSnackbar.message'),
        color: 'success',
        closeText: $i18n.t('close'),
      })
    }
    catch (error) {
      log.warn('Failed to rename shopping list group', { error })
    }
    finally {
      isLoading.value = false
    }
  }

  async function updateProductQuantity(productHandle: string, quantity: number, groupName: string) {
    if (!unref(accessToken)) return
    if (!productHandle || quantity < 0 || !groupName) {
      return
    }

    try {
      const response = await withAuthRetry(() => $fetch<ShoppingListRes>('/api/intern/shopping-list', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${unref(accessToken)}` },
        query: { cHandle: unref(customerHandle) },
        baseURL,
        body: {
          group: groupName,
          product: {
            handle: productHandle,
            quantity,
          },
        },
        onResponseError({ response }) {
          if (response.status !== 401) log.warn('Failed to update product quantity in shopping list', { responseStatus: response.status })
          if (response.status === 403) {
            _setDefaultValues()
          }
        },
      }))

      if (response) {
        mergeShoppingListsWithResponse(shoppingLists, includeAllowedSuppliers(response, new Set(availableSuppliers.value)))
      }
    }
    catch (error) {
      log.warn('Failed to update product quantity', { error, productHandle, quantity, groupName })
    }
  }

  function _setDefaultValues() {
    groupNames.value = []
    shoppingLists.value = []
    availableSuppliers.value = []
    isLoading.value = false
    initialFetchError.value = null
  }

  function $reset() {
    _setDefaultValues()
    refreshInitialFetch()
  }

  return {
    isLoading,
    groupNames,
    availableSuppliers,
    shoppingLists,
    refreshInitialFetch,
    initialFetchError,
    load,
    addToShoppingList,
    removeFromShoppingList,
    renameShoppingListGroup,
    deleteShoppingListGroup,
    updateProductQuantity,
    $reset,
  }
})
